import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const DailyReportPage = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [visibleColumns, setVisibleColumns] = useState({
    day: true,
    date: true,
    new_subscribers: true,
    amount_collected_inc_gst: true,
    amount_collected_ex_gst: true,
    total_orders_assigned: true,
    total_orders_evaluated: true,
    total_payout_eligible_amount: true,
    total_withdrawn_amount: true,
    eod_balance: true,
  });

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchDailyReport = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = "/employee-login";
        return;
      }

      try {
        const response = await axios.get(`${apiBaseURL}/api/daily-report`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReportData(response.data);
      } catch (err) {
        setError('Failed to fetch daily report.');
      } finally {
        setLoading(false);
      }
    };

    fetchDailyReport();
  }, [apiBaseURL]);

  const formatDateToIST = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const sortTable = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });

    const sortedData = [...reportData].sort((a, b) => {
      if (key === 'date') {
        return direction === 'asc' ? new Date(a[key]) - new Date(b[key]) : new Date(b[key]) - new Date(a[key]);
      } else {
        const valA = isNaN(a[key]) ? a[key] : parseFloat(a[key]);
        const valB = isNaN(b[key]) ? b[key] : parseFloat(b[key]);
        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
      }
    });
    setReportData(sortedData);
  };

  const toggleColumn = (column) => {
    setVisibleColumns((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  const getTotal = (key) =>
    reportData.reduce((sum, row) => sum + parseFloat(row[key] || 0), 0);
const downloadExcel = () => {
  const now = new Date();
  const formattedDateTime = now.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

  const headerRows = [
    ["OrderMinds Freelancing Solutions LLP"],
    ["Daily Report"],
    [`Downloaded on: ${formattedDateTime}`],
    [], // Empty row for spacing
  ];

  const dataToExport = reportData.map((row, index) => {
    const amountCollectedExGST = parseFloat(row.amount_collected_inc_gst || 0) / 1.18;
    const eodBalance = amountCollectedExGST - parseFloat(row.total_payout_eligible_amount || 0);

    return {
      "Day #": index + 1,
      Date: formatDateToIST(row.date),
      "New Subscribers": row.new_subscribers,
      "Amount Collected (Inc GST)": parseFloat(row.amount_collected_inc_gst).toFixed(2),
      "Amount Collected (Ex GST)": amountCollectedExGST.toFixed(2),
      "Orders Assigned": row.total_orders_assigned,
      "Orders Evaluated": row.total_orders_evaluated,
      "Payout Eligible": parseFloat(row.total_payout_eligible_amount).toFixed(2),
      "Payout Withdrawn": parseFloat(row.total_withdrawn_amount).toFixed(2),
      "EoD Balance": eodBalance.toFixed(2),
    };
  });

  const ws = XLSX.utils.json_to_sheet([]);
  XLSX.utils.sheet_add_aoa(ws, headerRows, { origin: "A1" });
  XLSX.utils.sheet_add_json(ws, dataToExport, { origin: `A${headerRows.length + 1}`, skipHeader: false });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");

  const fileName = `Daily_Report_${now.toISOString().replace(/[:.]/g, '-')}.xlsx`;
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(data, fileName);
};

  if (loading) return <div>Loading Daily Report...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="p-6 ml-72">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">Daily Report</h2>
        <button
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          onClick={downloadExcel}
        >
          Download Excel
        </button>
      </div>

      {/* Column Toggle */}
      <div className="mb-4 flex flex-wrap gap-4">
        {Object.keys(visibleColumns).map((col) => (
          <label key={col} className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={visibleColumns[col]}
              onChange={() => toggleColumn(col)}
            />
            <span className="capitalize">{col.replace(/_/g, ' ')}</span>
          </label>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="table-auto border-collapse w-full text-sm text-left">
          <thead>
            <tr className="bg-gray-200">
              {visibleColumns.day && <th className="border p-2">Day #</th>}
              {visibleColumns.date && <th className="border p-2">Date</th>}
              {visibleColumns.new_subscribers && <th className="border p-2">New Subscribers</th>}
              {visibleColumns.amount_collected_inc_gst && <th className="border p-2">Amount Collected (Inc GST)</th>}
              {visibleColumns.amount_collected_ex_gst && <th className="border p-2">Amount Collected (Ex GST)</th>}
              {visibleColumns.total_orders_assigned && <th className="border p-2">Orders Assigned</th>}
              {visibleColumns.total_orders_evaluated && <th className="border p-2">Orders Evaluated</th>}
              {visibleColumns.total_payout_eligible_amount && <th className="border p-2">Payout Eligible</th>}
              {visibleColumns.total_withdrawn_amount && <th className="border p-2">Payout Withdrawn</th>}
              {visibleColumns.eod_balance && <th className="border p-2">EoD Balance</th>}
            </tr>
          </thead>
          <tbody>
            {reportData.map((row, index) => {
              const amountCollectedExGST = parseFloat(row.amount_collected_inc_gst || 0) / 1.18;
              const eodBalance = amountCollectedExGST - parseFloat(row.total_payout_eligible_amount || 0);
              return (
                <tr key={index} className="hover:bg-gray-50">
                  {visibleColumns.day && <td className="border p-2 text-center">{index + 1}</td>}
                  {visibleColumns.date && <td className="border p-2">{formatDateToIST(row.date)}</td>}
                  {visibleColumns.new_subscribers && <td className="border p-2 text-center">{row.new_subscribers}</td>}
                  {visibleColumns.amount_collected_inc_gst && <td className="border p-2 text-right">₹ {parseFloat(row.amount_collected_inc_gst).toFixed(2)}</td>}
                  {visibleColumns.amount_collected_ex_gst && <td className="border p-2 text-right">₹ {amountCollectedExGST.toFixed(2)}</td>}
                  {visibleColumns.total_orders_assigned && <td className="border p-2 text-center">{row.total_orders_assigned}</td>}
                  {visibleColumns.total_orders_evaluated && <td className="border p-2 text-center">{row.total_orders_evaluated}</td>}
                  {visibleColumns.total_payout_eligible_amount && <td className="border p-2 text-right">₹ {parseFloat(row.total_payout_eligible_amount).toFixed(2)}</td>}
                  {visibleColumns.total_withdrawn_amount && <td className="border p-2 text-right">₹ {parseFloat(row.total_withdrawn_amount).toFixed(2)}</td>}
                  {visibleColumns.eod_balance && <td className="border p-2 text-right">₹ {eodBalance.toFixed(2)}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DailyReportPage;
